import { gql } from "@apollo/client";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import CsvAiPromptBox from "controls/CsvAiPromptBox";
import CsvPreviewTable, {
  csvRequiredColumnsMessage,
  csvToAssetUploader,
  missingCsvHeaders,
  parseCsv,
} from "controls/CsvPreviewTable";
import LinkButton from "controls/LinkButton";
import Message from "controls/Message";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import UploaderInput from "inputs/UploaderInput";
import { FileUpload } from "mdi-material-ui";
import React, { useState } from "react";

export const GAME_SCHEDULE_CSV_HEADERS = [
  "League Name",
  "Court Name",
  "Home Team Name",
  "Away Team Name",
  "Date",
  "Time",
  "Duration",
];

function getTemplateDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const TEMPLATE_ROW = ["Example League", "Court 1", "Team A", "Team B", getTemplateDate(), "18:00", "45"];

export function downloadGameScheduleTemplate() {
  const csv = [GAME_SCHEDULE_CSV_HEADERS.join(","), TEMPLATE_ROW.join(",")].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "game_schedules_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const COLUMN_VALIDATORS = {
  "League Name": (v) => (!v.trim() ? "Cannot be empty" : null),
  "Court Name": (v) => (!v.trim() ? "Cannot be empty" : null),
  "Home Team Name": (v) => (!v.trim() ? "Cannot be empty" : null),
  "Away Team Name": (v) => (!v.trim() ? "Cannot be empty" : null),
  Date: (v) => {
    if (!v.trim()) return "Cannot be empty";
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v.trim())) return "Must be in dd/mm/yyyy format";
    return null;
  },
  Time: (v) => {
    if (!v.trim()) return "Cannot be empty";
    if (!/^\d{2}:\d{2}$/.test(v.trim())) return "Must be in HH:MM format";
    return null;
  },
  Duration: (v) => {
    const trimmed = v.trim();
    const n = Number(trimmed);
    if (!trimmed || isNaN(n) || !Number.isInteger(n)) return "Must be a whole number";
    if (n < 1 || n > 180) return "Must be between 1 and 180";
    return null;
  },
};

const COLUMN_TYPES = { Date: "date", Time: "time" };

const AI_PROMPT = {
  subject: "game schedule",
  headers: GAME_SCHEDULE_CSV_HEADERS,
  formatNotes: {
    "League Name": "required — the league name",
    "Court Name": "required — must match a court already set up in the system",
    "Home Team Name": "required",
    "Away Team Name": "required — must be different from the home team",
    Date: "required — format dd/mm/yyyy (e.g. 25/12/2026)",
    Time: "required — 24-hour HH:MM (e.g. 18:30)",
    Duration: "required — game length in whole minutes, between 1 and 180",
  },
  exampleRow: TEMPLATE_ROW,
};

function CourtListModal({ orgCourts, onClose }) {
  const byVenue = orgCourts
    .filter((c) => c.active)
    .reduce((acc, court) => {
      const venueName = court.venue?.name ?? "No Venue";
      if (!acc[venueName]) acc[venueName] = [];
      acc[venueName].push(court.name);
      return acc;
    }, {});
  const venueNames = Object.keys(byVenue).sort();

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>Available Courts</DialogTitle>
      <DialogContent>
        {venueNames.length === 0 ? (
          <div style={{ color: "#777" }}>No courts found.</div>
        ) : (
          venueNames.map((venueName) => (
            <div key={venueName} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: "bold", marginBottom: 4 }}>Venue: {venueName}</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {byVenue[venueName].sort().map((name) => (
                  <li key={name}>Court: {name}</li>
                ))}
              </ul>
            </div>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

ImportGameScheduleCsvDialog.useButtonProps = ({ orgId }) => {
  // Importing requires an org context; hide the button on the global schedules page.
  if (!orgId) return null;
  return { icon: <FileUpload />, content: "Import Game Schedules CSV" };
};

export default function ImportGameScheduleCsvDialog({
  orgId,
  orgTimeZone: orgTimeZoneProp,
  orgCourts: orgCourtsProp,
  onClose,
}) {
  const [phase, setPhase] = useState("upload"); // 'upload' | 'queued'
  const [courtListOpen, setCourtListOpen] = useState(false);
  const [uploaderValue, setUploaderValue] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState(null);
  const [csvRows, setCsvRows] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [orgData] = useData(
    gql`
      query ImportGameScheduleCsvDialog($orgId: ID!) {
        org(id: $orgId) {
          id
          timeZone
          courts {
            id
            name
            active
            venue {
              id
              name
            }
          }
        }
      }
    `,
    { orgId },
    { skip: orgTimeZoneProp != null && orgCourtsProp != null },
  );

  const orgTimeZone = orgTimeZoneProp ?? orgData?.org.timeZone;
  const orgCourts = orgCourtsProp ?? orgData?.org.courts ?? [];

  const importCsv = useActionFragment("importGameScheduleCsv", `import { id imported importErrorMessage }`);

  const handleUploaderChange = async (value) => {
    setUploaderValue(value);
    setCsvHeaders(null);
    setCsvRows(null);
    setErrorMessage(null);
    if (!value?.blobUrl) return;
    try {
      const text = await fetch(value.blobUrl).then((r) => r.text());
      const { headers, rows } = parseCsv(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
    } catch (e) {
      setErrorMessage(`Could not read CSV file: ${e.message}`);
    }
  };

  const MAX_ROWS = 500;
  // Large files skip the client-side preview and validation entirely; the backend will still validate every row.
  const previewHidden = csvRows && csvRows.length > MAX_ROWS;

  const missingHeaders = missingCsvHeaders(GAME_SCHEDULE_CSV_HEADERS, csvHeaders);
  const headerError = missingHeaders.length
    ? `This file is missing required column(s): ${missingHeaders.join(", ")}. Please upload a CSV that matches the template.`
    : null;

  const hasValidationErrors =
    !previewHidden &&
    (csvRows?.some((row) => csvHeaders?.some((h, i) => COLUMN_VALIDATORS[h]?.(row[i] ?? "") != null)) ?? false);

  const canSubmit = csvHeaders && csvRows && csvRows.length > 0 && !headerError && !hasValidationErrors && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const assetUploader = csvToAssetUploader(csvHeaders, csvRows, uploaderValue.name);

      await importCsv({ input: { orgId, assetUploader } }, { suppressError: true });
      setPhase("queued");
    } catch (error) {
      setErrorMessage(error.message.replace("GraphQL error: ", ""));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open
        maxWidth="lg"
        fullWidth
        scroll="body"
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          onClose?.();
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>
          <div style={{ fontSize: "1.2em", fontWeight: "bold" }}>Import Game Schedules CSV</div>

          {phase === "upload" && (
            <>
              <Message
                type="info"
                content={
                  <>
                    {csvRequiredColumnsMessage(GAME_SCHEDULE_CSV_HEADERS)} <br />
                    You can <LinkButton onClick={downloadGameScheduleTemplate}>download</LinkButton> the template CSV
                    and <LinkButton onClick={() => setCourtListOpen(true)}>view</LinkButton> all available court names.{" "}
                    <br /> <br />
                    Visit our{" "}
                    <a
                      href="https://docs.gloryleague.basketball/article/213-upload-schedule"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      upload schedule guide
                    </a>{" "}
                    for step-by-step instructions and use the AI prompt message to help you with formatting.
                  </>
                }
              />

              <CsvAiPromptBox {...AI_PROMPT} />

              {errorMessage && <Message type="error" content={errorMessage} />}

              <UploaderInput
                name="assetUploader"
                label="CSV Upload"
                value={uploaderValue}
                onChange={handleUploaderChange}
                accept=".csv"
              />

              {headerError && <Message type="error" content={headerError} />}

              {csvHeaders && csvRows && (
                <>
                  <div style={{ fontSize: "0.85em", color: "#555" }}>
                    Date and time are interpreted in your org&apos;s timezone: <strong>{orgTimeZone}</strong>
                  </div>
                  {previewHidden ? (
                    <Message
                      type="info"
                      content={`Preview unavailable — file has ${csvRows.length.toLocaleString()} rows. Every row will still be validated during import, and you'll be emailed a report of any that couldn't be processed.`}
                    />
                  ) : (
                    <CsvPreviewTable
                      headers={csvHeaders}
                      rows={csvRows}
                      onChange={setCsvRows}
                      columnValidators={COLUMN_VALIDATORS}
                      columnTypes={COLUMN_TYPES}
                      maxRows={MAX_ROWS}
                    />
                  )}
                </>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit}>
                  {submitting ? "Uploading…" : "Confirm & Import"}
                </Button>
                <Button onClick={onClose} color="inherit">
                  Cancel
                </Button>
              </div>
            </>
          )}

          {phase === "queued" && (
            <>
              <Message
                type="success"
                content="Your import has been queued for processing. We'll email you if any errors are found, otherwise you can expect the new game schedules to appear in the system shortly!"
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" onClick={onClose}>
                  Done
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {courtListOpen && <CourtListModal orgCourts={orgCourts} onClose={() => setCourtListOpen(false)} />}
    </>
  );
}
